// contractor (freelance) work agreement for Norway region (both English and Norwegian language)
import { Typography } from '@material-ui/core'
import { Fragment } from 'react'
import style from '../modals/modals.module.css'

type Values = {
  today: string
  description: string
  jobType: string
  registeredName?: string
  businessName: string
  orgNumber: string
  businessAddress: string
  stafferNameFull: string
  identityNumber: string
  endDate: string
  jobDateStart: string
  jobDateEnd: string
  jobHourlyWage: string
  jobRequirementsTranslated: Record<'en' | 'no', Array<string>>
  jobTypeTranslated: Record<'en' | 'no', string>
  salaryHourly: number
}

const English = ({
  today,
  description,
  jobType,
  jobRequirementsTranslated,
  registeredName,
  businessName,
  orgNumber,
  businessAddress,
  stafferNameFull,
  identityNumber,
  endDate,
  jobDateStart,
  jobDateEnd,
  jobHourlyWage,
}: Values) => (
  <Fragment>
    <Typography component="h6" variant="h6" className={style.agreementHeader}>
      Contractor Agreement
    </Typography>
    <div className={style.agreementWrapper}>
      <p>
        This contractor agreement (the agreement) is signed on {today} between
        <br />
        <br />
        the principal which is apparent in <strong>appendix 1</strong> and
        <br />
        <br />
        the contractor which is apparent in <strong>appendix 2</strong>,
        <br />
        <br />
        according to, at all time, applicable terms, apparent in <strong>appendix 3</strong>
        <br />
        <br />
        {description && (
          <p>
            <strong>Introductory comment</strong>
            <br />
            {description}
            <br />
            <br />
          </p>
        )}
        <strong>The scope of the agreement</strong>
        <br />
        The contractor has by agreeing to this agreement, agreed to complete the assignment of {jobType} for the
        principal.
        <br />
        <br />
        The assignment involves that the contractor is going to perform the following tasks for the principal:
        <br />
        {jobRequirementsTranslated.en.length > 0 && (
          <Fragment>
            {jobRequirementsTranslated.en.map((text) => `	 - ${text} \n`)}
            <br />
          </Fragment>
        )}
        The contractor commits to deliver the services in this agreement in accordance with best practice for similar
        services. The contractor confirms to have the necessary qualifications and relevant experience to deliver the
        services.
        <br />
        <br />
        <strong>The agreement ́s length and termination</strong>
        <br />
        This agreement is valid until {endDate}, alternatively until the services are delivered and the correct
        compensation is received. If the any of the parties wishes to cancel the agreement at an earlier time, this has
        to be done at least 24 hours by written notice. If one of the parties breaches their duties of the contract, the
        other party can at once terminate the agreement.
        <br />
        <br />
        <strong>Lateness</strong>
        <br />
        In the case the freelancer is more than 10 minutes late for an assignment, the principal has the right to
        immediately terminate the assignment and have the right to dismiss the contractor from the workplace.
        <br />
        <br />
        <strong>Price/invoicing</strong>
        <br />
        Assignments according to this agreement are calculated after time used and hourly wage per hour / excluding mva,
        based on the specified hours for each day of the contract. The compensation is not to be regulated within the
        agreement period, and includes all costs of the contractor unless otherwise is specified.
        <br />
        <br />
        <strong>Insurance</strong>
        <br />
        The Contractor is covered by Payout Partner AS´ Occupational Injury and Disease insurance policy provided by LY
        Forsikring. This insurance (yrkeskadeforsikring/yrkesykdom) covers all incidents that might occur during the
        assignment. The contractor will be deducted NOK 1,6/h to cover the insurance cost.
        <br />
        The contractor is not a part of the Principal´s insurance plan.
        <br />
        <br />
        <strong>Bi-costs</strong>
        <br />
        a. Documented expenses and travel/living expenses related to the assignment are to be refunded to the contractor
        if agreed between the parties.
        <br />
        <br />
        <strong>Confidentiality</strong>
        <br />
        Both parties shall obtain mutual confidentiality about the information they obtain about the other party in
        connection with the completion of this agreement. The confidentiality clause does not apply to public known
        information. The confidentiality clause applies also after the assignment is complete.
        <br />
        <br />
        <strong>Force Majeure</strong>
        <br />
        The parties are not responsible for delays or un-completion of the assignment due to circumstances that are
        beyond their control.
        <br />
        <br />
        <strong>Passitivity</strong>
        <br />
        No demands associated with the agreement can be put forward later than 2 years after the reason has been put
        forward.
        <br />
        <br />
        <strong>Governing law and venue</strong>
        <br />
        By disputes that occur in relation the agreement, Norwegian law applies, and the dispute is to be resolved by
        the Norwegian courts.
        <br />
        <br />
        This agreement is formed and signed in 2 copies, on to each party.
        <br />
      </p>
    </div>
  </Fragment>
)

const Norwegian = ({
  today,
  description,
  jobType,
  jobRequirementsTranslated,
  registeredName,
  businessName,
  orgNumber,
  businessAddress,
  stafferNameFull,
  identityNumber,
  endDate,
  jobDateStart,
  jobDateEnd,
  jobHourlyWage,
}: Values) => (
  <Fragment>
    <Typography component="h6" variant="h6" className={style.agreementHeader}>
      OPPDRAGSAVTALE
    </Typography>
    <div className={style.agreementWrapper}>
      <p>
        Denne oppdragsavtalen (Avtalen) er inngått den {today} mellom:
        <br />
        <br />
        Oppdragsgiver som fremgår av <strong>vedlegg 1</strong> og
        <br />
        <br />
        Oppdragstaker som fremgår av <strong>vedlegg 2</strong>,
        <br />
        <br />i henhold til enhver tid gjeldende vilkår som fremgår av <strong>vedlegg 3</strong>.
        <br />
        <br />
        {description && (
          <p>
            <strong>Innledende kommentar</strong>
            <br />
            Her kan man skrive litt om bakgrunnen for avtalen, eller generelt om prosjektet som konsulenten skal være en
            del av.
            <br />
            <br />
          </p>
        )}
        <strong>Avtalens omfang</strong>
        <br />
        Oppdragstaker har ved inngåelse av Avtalen akseptert å utføre oppdrag om {jobType} for Oppdragsgiver.
        <br />
        <br />
        {jobRequirementsTranslated.no.length > 0 && (
          <Fragment>
            Oppdraget innebærer at Oppdragstaker skal utføre følgende oppgaver for Oppdragsgiver:
            <br />
            {jobRequirementsTranslated.no.map(
              (text) => `\t - ${text}\u00A0\n` // Added missing newline character
            )}
            <br />
          </Fragment>
        )}
        Oppdragstaker forplikter seg til å levere tjenestene under denne avtale i samsvar med beste praksis for
        tilsvarende tjenester. Oppdragstaker bekrefter å inneha de nødvendige kvalifikasjoner og relevant erfaring for
        levering av tjenestene.
        <br />
        <br />
        <strong>Avtalens varighet og opphør</strong>
        <br />
        Denne avtalen gjelder frem til {endDate}, alternativt til tjenestene er levert og rett oppgjør er mottatt.
        <br />
        Dersom noen av partene ønsker å si opp avtalen på et tidligere tidspunkt, skal dette skje minst 24 timer før
        oppdraget med skriftlig varsel. Oppdragstaker har i tilfelle avbestilling rett til ethvert opptjent vederlag
        eller pådratt utlegg i forbindelse med avtalen, frem til oppsigelsestidens utløp.
        <br />
        <br />
        Eventuelt: Dersom en av partene vesentlig misligholder sine plikter etter Avtalen, kan den andre parten
        umiddelbart si opp Avtalen med skriftlig varsel.
        <br />
        <br />
        <strong>Forsentkomming</strong>
        <br />
        I tilfelle hvor oppdragstaker kommer mer enn 10 minutter for sent til et oppdrag, har oppdragsgiver rett til å
        si opp avtalen med umiddelbar virking og bortvise oppdragstaker fra arbeidsstedet.
        <br />
        <br />
        <strong>Pris/fakturering</strong>
        <br />
        Oppdrag i henhold til denne avtalen avregnes etter medgått tid og beløp i kr/t ekskl. mva, basert på
        spesifiserte timelister for hver dag det er arbeidet. Honorarsatsen er ikke gjenstand for regulering i den
        planlagte avtaleperioden, og inkluderer alle-kostnader for Oppdragstaker med mindre annet er særskilt
        spesifisert nedenfor.
        <br />
        <br />
        <strong>Forsikring</strong>
        <br />
        Oppdragstaker er dekket av Payout Partner AS sin yrkeskadeforsikring/yrkesykdom levert av LY Forsikring.
        Oppdragstaker vil bli trukket NOK 1,6/t for å dekke denne forsikringen. Oppdragstaker er ikke dekket av
        oppdragsgivers yrkeskadeforskring.
        <br />
        <br />
        <strong>Bi-omkostninger</strong>
        <br />
        a. Dokumenterte utlegg og reise-/oppholdskostnader relatert til oppdraget refunderes uten påslag dersom dette er
        avtalt mellom partene.
        <br />
        b. Bruk og parkering av egen bil godtgjøres etter Statens satser og registrert parkeringsavgift.
        <br />
        <br />
        <strong>Konfidensialitet</strong>
        <br />
        Partene har gjensidig konfidensialitetsplikt med hensyn til all informasjon de erverver om den annen part i
        forbindelse med inngåelse og utførelse av denne avtale. Konfidensialitetsplikten gjelder ikke forhold som er
        alminnelig kjent.
        <br />
        Konfidensialitetsplikten gjelder også etter utløpet av avtalen.
        <br />
        <br />
        <strong>Force Majeure</strong>
        <br />
        Partene er ikke ansvarlige for forsinkelser eller ikke-utførte handlinger som skyldes forhold utenfor partneres
        kontroll.
        <br />
        <br />
        <strong>Passivitet</strong>
        <br />
        Ingen krav knyttet til Avtalen kan fremsettes mer enn to år etter at grunnen for å fremsette kravet oppstod.
        <br />
        <br />
        <strong>Lovvalg og verneting</strong>
        <br />
        Ved eventuelle tvister som oppstår i relasjon til Avtalen, gjelder norsk rett, og tvisten skal avgjøres ved
        norske domstoler.
        <br />
        <br />
        Denne avtalen er utformet og signert i 2 eksemplarer; ett til hver av partene.
        <br />
        <br />
        <strong>Vedlegg 1</strong>
        <br />
        <strong>{registeredName || businessName}</strong>
        <br />
        Org. nr.: <strong>{orgNumber}</strong>
        <br />
        Adresse: <strong>{businessAddress}</strong>
        <br />
        <br />
        <strong>Vedlegg 2</strong>
        <br />
        <strong>{stafferNameFull}</strong>
        <br />
        Personnr.: <strong>{identityNumber}</strong>
        <br />
        <br />
        <strong>Vedlegg 3</strong>
        <br />
        Jobbsted: <strong>{businessAddress}</strong>
        <br />
        Starttid: <strong>{jobDateStart}</strong>
        <br />
        Sluttid: <strong>{jobDateEnd}</strong>
        <br />
        Lønn: <strong>{jobHourlyWage}</strong>
        <br />
        Stilling: <strong>{jobType}</strong>
      </p>
    </div>
  </Fragment>
)

export default {
  English,
  Norwegian,
}
